import { extractImageFrames, clampTo } from '../../tools';
import type { baiduModelConfig } from '../types';


export const viduConfigs: Record<string, baiduModelConfig> = {
    'viduq3-turbo': {
        // https://tcnxnzs113h4.feishu.cn/wiki/LqbZwxqoWi5LaXk4341cfFZ7ncg
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first') {
                return '/v3/aigc/vd/ent/v2/img2video';
            } else if (frameMode === 'first_last') {
                return '/v3/aigc/vd/ent/v2/start-end2video';
            } else if (frameMode === 'ref') {
                return '/v3/aigc/vd/ent/v2/reference2video';
            } else {
                return '/v3/aigc/vd/ent/v2/text2video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'task_id',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'viduq3-turbo',
                    prompt: prompt.slice(0, 5000),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    images: [firstImage],
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    is_rec: false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    audio_type: 'all',
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'viduq3-turbo',
                    prompt: prompt.slice(0, 5000),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首尾帧 URL
                    images: [firstImage, lastImage],
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    is_rec: false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    model: 'viduq3-turbo',
                    prompt: prompt.slice(0, 5000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 参考主体（最多 7 个）
                    subjects: (imagePrompt || [])
                        .filter((img) => !!img)
                        .slice(0, 7)
                        .map((img, idx) => ({
                            name: `image${idx + 1}`,
                            images: [img],
                        })),
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    auto_subjects: false,
                    audio_type: 'all',
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'viduq3-turbo',
                    prompt: prompt.slice(0, 5000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                }
            }
        },
    },
    'viduq3': {
        // https://tcnxnzs113h4.feishu.cn/wiki/LqbZwxqoWi5LaXk4341cfFZ7ncg
        apiPath: '/v3/aigc/vd/ent/v2/reference2video',
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'task_id',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            // 参考模式
            return {
                model: 'viduq3',
                prompt: prompt.slice(0, 5000),
                aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1'] as const, '16:9'),
                duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                // 参考 URL
                subjects: (imagePrompt || [])
                    .filter((img) => !!img)
                    .slice(0, 7)
                    .map((img, idx) => ({
                        name: `image${idx + 1}`,
                        images: [img],
                    })),
                // 可选参数
                audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                // system
                auto_subjects: false,
                audio_type: 'all',
                payload: undefined,
                off_peak: false,
                meta_data: undefined,
                callback_url: undefined,
            };
        },
    },
    'viduq3-pro': {
        // https://tcnxnzs113h4.feishu.cn/wiki/LqbZwxqoWi5LaXk4341cfFZ7ncg
        apiPath: (params) => {
            const { frameMode } = params;
            if (frameMode === 'first') {
                return '/v3/aigc/vd/ent/v2/img2video';
            } else if (frameMode === 'first_last') {
                return '/v3/aigc/vd/ent/v2/start-end2video';
            } else if (frameMode === 'ref') {
                return '/v3/aigc/vd/ent/v2/reference2video';
            } else {
                return '/v3/aigc/vd/ent/v2/text2video';
            }
        },
        pollApiPath: '/v3/tasks/{id}',
        statusMapping: {
            taskIdPath: 'task_id',
            success: ['SUCCESS'],
            failed: ['FAILED'],
            processing: ['RUNNING'],
            statePath: 'status',
            videoUrlPath: 'videoGenerateTaskInfo.videoGenerateTaskOutput.mediaBasicInfos[0].source.sourceUrl',
            errorPath: 'videoGenerateTaskInfo.errMsg',
        },
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'viduq3-pro',
                    prompt: prompt.slice(0, 5000),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首帧 URL
                    images: [firstImage],
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    is_rec: false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    audio_type: 'all',
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'viduq3-pro',
                    prompt: prompt.slice(0, 5000),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 首尾帧 URL
                    images: [firstImage, lastImage],
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    is_rec: false,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'viduq3-pro',
                    prompt: prompt.slice(0, 5000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '4:3', '3:4', '1:1'] as const, '16:9'),
                    duration: clampTo(duration || 5, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const, 5),
                    resolution: clampTo(quality || '720p', ['540p', '720p', '1080p'] as const, '720p'),
                    seed: typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined,
                    // 可选参数
                    audio: typeof providerOptions?.audio === 'boolean' ? providerOptions.audio : true,
                    watermark: typeof providerOptions?.watermark === 'boolean' ? providerOptions.watermark : false,
                    wm_position: typeof providerOptions?.wmPosition === 'string' ? providerOptions.wmPosition : undefined,
                    wm_url: typeof providerOptions?.wmUrl === 'string' ? providerOptions.wmUrl : undefined,
                    // system
                    payload: undefined,
                    off_peak: false,
                    meta_data: undefined,
                    callback_url: undefined,
                }
            }
        },
    },
}